"use client";

import React, { useEffect, useState } from "react";
import { AgenticToolCallRow } from "@/components/ui/agentic-tool-call-row";
import { ChatDataTableCard } from "./ChatDataTableCard";
import { CHAT_TOOL_STEP_DETAIL_PANEL_CLASS } from "./chatToolStepDetailUi";
import type { ChatDataTableEmbed } from "./types";
import { isDeliverableDataTableEmbed } from "./dataTableEmbedUi";
import { ToolStepRow, parseToolStep, type ToolStep } from "./ToolStepRow";

type ToolStepDataTableEmbedRowProps = {
  toolSegment: Record<string, unknown>;
  dataTableEmbeds: ChatDataTableEmbed[];
  messageId?: string;
  onReplayFeTool?: (toolId: string, params: Record<string, unknown>) => void;
};

function stepFromSegment(toolSegment: Record<string, unknown>): ToolStep | null {
  return parseToolStep(toolSegment);
}

function mapStatus(status?: string) {
  if (status === "error") return "error" as const;
  if (status === "pending") return "pending" as const;
  if (status === "running") return "running" as const;
  if (status === "cancelled") return "cancelled" as const;
  return "success" as const;
}

function displayLabel(step: ToolStep): string {
  return step.label || step.tool_label || step.tool_name;
}

/** One row: tool-step label/summary plus expandable data table cards. */
export function ToolStepDataTableEmbedRow({
  toolSegment,
  dataTableEmbeds,
  messageId,
  onReplayFeTool,
}: ToolStepDataTableEmbedRowProps) {
  const step = stepFromSegment(toolSegment);
  const deliverable = dataTableEmbeds.some(isDeliverableDataTableEmbed);
  const hasTables = dataTableEmbeds.length > 0;
  const [expanded, setExpanded] = useState(deliverable);

  useEffect(() => {
    setExpanded(deliverable);
  }, [deliverable]);

  if (!step) {
    return null;
  }

  const replayable =
    Boolean(step.fe_tool_id) &&
    step.fe_tool_params &&
    typeof onReplayFeTool === "function";

  const showTables = hasTables && (deliverable || expanded);
  const tableDetail = showTables ? (
    <div className={CHAT_TOOL_STEP_DETAIL_PANEL_CLASS} onClick={(event) => event.stopPropagation()}>
      {dataTableEmbeds.map((embed, index) => (
        <ChatDataTableCard key={`${messageId ?? "table"}-${index}`} embed={embed} density="toolDetail" />
      ))}
    </div>
  ) : null;

  if (!hasTables) {
    return <ToolStepRow step={step} onReplayFeTool={onReplayFeTool} />;
  }

  const activateHeader = () => {
    if (replayable && step.fe_tool_id && step.fe_tool_params) {
      onReplayFeTool?.(step.fe_tool_id, step.fe_tool_params);
      return;
    }
    setExpanded((open) => !open);
  };

  return (
    <div
      data-testid="chat-tool-step-data-table-embed"
      data-presentation={deliverable ? "deliverable" : "step"}
    >
      <AgenticToolCallRow
        toolName={step.tool_name}
        label={displayLabel(step)}
        status={mapStatus(step.status)}
        hidePrefix={Boolean(step.kind)}
        summary={step.summary}
        detail={tableDetail}
        detailClassName="!ml-3"
        className="!pl-0"
        data-testid="chat-tool-step"
        onHeaderActivate={activateHeader}
        headerAriaExpanded={showTables}
      />
    </div>
  );
}
